import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import type { ComputedRef } from 'vue'

import { ReportSummary } from '@/modules/reports/interfaces/reportData'
import { getHotelReport } from '@/modules/reports/services/hotelReportApi'

interface Params {
	companyId?: number
	filters?: Filters
}

interface Filters {
	dates?: string[] | null
	creationDates?: string[] | null
	status?: string
	groupBy?: string
	travelerId?: number
	hotelId?: number
	createdAt?: string[] | null
}

const getCompanyReportHotelsSummary = async (
	companyId: number,
	filters?: Filters
): Promise<ReportSummary> => {
	const { data } = await getHotelReport<ReportSummary>(companyId, 'summary', {
		params: {
			checkOutDates: filters?.dates,
			createdDate: filters?.creationDates,
			status: filters?.status,
			groupBy: filters?.groupBy,
			travelerId: filters?.travelerId,
			hotelId: filters?.hotelId,
			createdAt: filters?.createdAt,
		},
	})

	return data
}

const useCompanyReportHotelsSummary = (params: ComputedRef<Params>) => {
	const queryKey = computed(() => [
		'reportSummary',
		params.value.companyId,
		params.value.filters?.dates,
		params.value.filters?.creationDates,
		params.value.filters?.status,
		params.value.filters?.groupBy,
		params.value.filters?.travelerId,
		params.value.filters?.hotelId,
		params.value.filters?.createdAt,
	])

	const enabled = computed(() => !!params.value.companyId)

	const { data, isLoading, isFetching, refetch } = useQuery({
		queryKey,
		queryFn: () => {
			return getCompanyReportHotelsSummary(
				params.value.companyId!,
				params.value.filters
			)
		},
		enabled,
		staleTime: 0,
		cacheTime: 0,
		retry: false,
		refetchOnWindowFocus: false,
	})

	return {
		reportSummary: data,
		isLoading: computed(
			() => enabled.value && (isLoading.value || isFetching.value)
		),
		isFetching,
		refetch,
	}
}

export default useCompanyReportHotelsSummary
